import { useCallback, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { IconButton, Tooltip } from "@mui/material";
import ScreenShareIcon from "@mui/icons-material/ScreenShare";
import StopScreenShareIcon from "@mui/icons-material/StopScreenShare";

import { RootContext } from "../context/root-provider";
import { onShareApp, startShare, stopShare } from "../services/zoomMeeting";
import { TOOL_BTN_SX, TOOL_BTN_ACTIVE_SX } from "../liveTap/liveChrome";

// Share to meeting (TT-1207 F3): inside a Zoom meeting, a toggle that shows
// this panel to everyone in it, view-only, through Zoom's own app share. What
// goes out is whatever the panel shows. Zoom's onShareApp event drives the
// state, so a share ended from Zoom's controls resets the toggle too.
//
// Two coats: the Live Tap toolbar's icon button, or, given a className, a
// labelled button in a page header's own style (the Tapestry detail page, F4:
// a past Tapestry shown to the meeting).

const ZoomShareButton = ({ onError, className }) => {
  const { inMeeting } = useContext(RootContext);
  const [sharing, setSharing] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!inMeeting) return undefined;
    return onShareApp((action) => setSharing(action === "start"));
  }, [inMeeting]);

  const toggle = useCallback(async () => {
    setBusy(true);
    try {
      if (sharing) {
        await stopShare();
        setSharing(false);
      } else {
        await startShare();
        setSharing(true);
      }
    } catch (err) {
      console.log("shareApp failed:", err);
      onError?.(`Could not ${sharing ? "stop sharing" : "share to the meeting"}: ${err?.message || err}`);
    } finally {
      setBusy(false);
    }
  }, [sharing, onError]);

  if (!inMeeting) return null;

  const [title, label] = sharing
    ? ["Stop sharing to the meeting", "Stop sharing"]
    : ["Share this Tapestry to the meeting", "Share to meeting"];
  const icon = sharing ? <StopScreenShareIcon /> : <ScreenShareIcon />;
  if (className) {
    return (
      <button className={className} onClick={toggle} disabled={busy} title={title} aria-pressed={sharing}>
        {icon} {label}
      </button>
    );
  }
  return (
    <Tooltip title={title}>
      <span>
        <IconButton
          onClick={toggle}
          disabled={busy}
          sx={sharing ? TOOL_BTN_ACTIVE_SX : TOOL_BTN_SX}
          aria-label={title}
          aria-pressed={sharing}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );
};

ZoomShareButton.propTypes = {
  /** Told what went wrong, in words for the user; the page shows it. */
  onError: PropTypes.func,
  /** A page header's button style; with it the button carries its label. */
  className: PropTypes.string,
};

export default ZoomShareButton;
